/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useState } from 'react';

import { bind } from 'bind-event-listener';

import { useSetColorMode } from '@atlaskit/app-provider/use-set-color-mode';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItemRadio from '@atlaskit/dropdown-menu/dropdown-item-radio';
import DropdownItemRadioGroup from '@atlaskit/dropdown-menu/dropdown-item-radio-group';
import ThemeIcon from '@atlaskit/icon/core/theme';
import { Inline, Stack, Text, UNSAFE_useMediaQuery } from '@atlaskit/primitives/compiled';
import type { ThemeColorModes } from '@atlaskit/tokens/theme-config';

import { ButtonTextWrapper } from '../navigation/utils/button-text-wrapper';
import { NavIconWrapper } from '../navigation/utils/nav-icon-wrapper';
import { Status } from '../status';
import { useUserPreferences } from '../user-preferences';

import DarkThumb from './images/DarkThumb.svg';
import LightThumb from './images/LightThumb.svg';
import ThemeThumbnail from './theme-thumbnail';

type ThemeConfig = {
	label: string;
	description?: string;
	image: string;
	status?: 'alpha' | 'beta';
};

const colorModeData: Record<ThemeColorModes, ThemeConfig> = {
	light: {
		label: 'Light',
		image: LightThumb,
	},
	dark: {
		label: 'Dark',
		image: DarkThumb,
	},
	auto: {
		label: 'Match browser',
		image: LightThumb,
	},
};

const isValidThemeColorMode = (theme: string): theme is ThemeColorModes =>
	Object.keys(colorModeData).includes(theme);

type ThemeControlsProps = {
	isOpen?: boolean;
};

/**
 * __Theme controls__
 *
 * A dropdown menu that allows users to select a theme.
 */
export const ThemeControls: (props: ThemeControlsProps) => JSX.Element = ({
	isOpen: controlledIsOpen,
}: ThemeControlsProps) => {
	const { userPreferences, setUserPreferences } = useUserPreferences();

	const [uncontrolledIsOpen, setIsUncontrolledOpen] = useState(false);
	const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
	const [colorMode, setColorMode] = useState<ThemeColorModes>('light');
	const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
	const [isBelowMd, setIsBelowMd] = useState<boolean>(false);

	const mediaQueryResult = UNSAFE_useMediaQuery('below.md', (event) => {
		setIsBelowMd(event.matches);
	});
	// Set initial value from media query result
	useEffect(() => {
		if (mediaQueryResult) {
			setIsBelowMd(mediaQueryResult.matches);
		}
	}, [mediaQueryResult]);

	const setAppProviderColorMode = useSetColorMode();

	// Set initial color mode based on user preferences from local storage
	useEffect(() => {
		const colorModePreference = userPreferences['colorMode'];
		if (colorModePreference && isValidThemeColorMode(colorModePreference)) {
			// If a light/dark/auto mode has previously been set, use that mode
			setColorMode(colorModePreference);
			setAppProviderColorMode(colorModePreference);
		} else {
			// If nothing set previously, set default mode to 'auto' and load tokens
			setColorMode('auto');
			setAppProviderColorMode('auto');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Watch for system color mode changes
	useEffect(() => {
		if (typeof window !== 'undefined' && window.matchMedia) {
			const unbind = bind(window.matchMedia('(prefers-color-scheme: dark)'), {
				type: 'change',
				listener: (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light'),
			});
			return unbind;
		}
	}, []);

	const handleColorModeChange = useCallback(
		(mode: ThemeColorModes) => {
			setColorMode(mode);
			setAppProviderColorMode(mode || colorMode);
			setUserPreferences({ colorMode: mode });
		},
		[colorMode, setAppProviderColorMode, setUserPreferences],
	);

	return (
		<DropdownMenu<HTMLButtonElement>
			placement="bottom-end"
			isOpen={isOpen}
			onOpenChange={() => setIsUncontrolledOpen(!isOpen)}
			trigger={({ triggerRef, onClick, ...triggerProps }) =>
				isBelowMd ? (
					<IconButton
						ref={triggerRef}
						icon={(props) => {
							const { color: _color, ...restProps } = props as React.ComponentProps<
								typeof ThemeIcon
							> & { color?: string };
							return (
								<NavIconWrapper>
									<ThemeIcon {...restProps} />
								</NavIconWrapper>
							);
						}}
						label="Set website theme"
						testId="theme"
						onClick={(e) => {
							onClick?.(e);
							setIsUncontrolledOpen(!isOpen);
						}}
						{...triggerProps}
					/>
				) : (
					<Button
						ref={triggerRef}
						iconBefore={(props) => {
							const { color: _color, ...restProps } = props as React.ComponentProps<
								typeof ThemeIcon
							> & { color?: string };
							return (
								<NavIconWrapper>
									<ThemeIcon {...restProps} />
								</NavIconWrapper>
							);
						}}
						onClick={(e) => {
							onClick?.(e);
							setIsUncontrolledOpen(!isOpen);
						}}
						{...triggerProps}
					>
						<ButtonTextWrapper>Theme</ButtonTextWrapper>
					</Button>
				)
			}
			shouldRenderToParent
		>
			<DropdownItemRadioGroup id="color-mode" title="Color mode">
				{Object.entries(colorModeData).map(([mode, metadata]) => {
					return (
						<DropdownItemRadio
							id={mode}
							key={mode}
							isSelected={colorMode === mode}
							onClick={() => handleColorModeChange(mode as ThemeColorModes)}
						>
							<Inline space="space.150" alignBlock="center">
								<ThemeThumbnail
									src={
										colorModeData[mode === 'auto' ? systemTheme : (mode as ThemeColorModes)].image
									}
								/>
								<Stack>
									<Text weight="medium">
										{metadata.label} {metadata.status && <Status type={metadata.status} />}
									</Text>
									{metadata.description && (
										<Text size="small" color="color.text.subtle">
											{metadata.description}
										</Text>
									)}
								</Stack>
							</Inline>
						</DropdownItemRadio>
					);
				})}
			</DropdownItemRadioGroup>
		</DropdownMenu>
	);
};
